import { Card, Checkbox, Divider, Flex, Skeleton, Text } from "components/common"
import {
  PAYMENTS_FORM_LIST_NAME,
  parsePaymentLineAmount,
  roundMoneyToTwoDecimals,
} from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/utils/paymentUtils"
import { Form } from "antd"
import { t } from "i18next"
import { useMemo } from "react"
import theme from "utils/themeVars"
import { FRACTIONAL_PAYMENT_CHANNEL_PSID, FRACTIONAL_PAYMENT_CHANNEL_STANDARD, resolvePsidPaymentModeKey } from "../fractionalPaymentUtils"
import { formatFractionalSaleAmount } from "../utilis/fractionalSaleCalculations"
import { freshFractionalPaymentRowAfterChannelToggle } from "../utilis"
import styles from "./fractionalBlocksSummary.module.less"

export default function FractionalBlocksSummary({
  blockCount,
  blocksTotal,
  taxTotal,
  partnerFeeTotal,
  grandTotal,
  feeLoading,
  isFiler,
  modeOfPayments,
  processingFee,
  form,
}) {
  const summarySkeleton = <Skeleton active height={20} width={72} />

  const rows = [
    {
      label: `${t("Property blocks")} (x${+blockCount > 0 ? blockCount : 1})`,
      value: formatFractionalSaleAmount(blocksTotal),
    },
    {
      label: t("Advance tax ({{filerStatus}})", {
        filerStatus: isFiler ? t("Filer") : t("Non Filer"),
      }),
      value: feeLoading ? summarySkeleton : formatFractionalSaleAmount(taxTotal),
    },
    {
      label: t("Processing fee"),
      value: formatFractionalSaleAmount(processingFee),
    },
    {
      label: t("Partner fee"),
      value: feeLoading ? summarySkeleton : formatFractionalSaleAmount(partnerFeeTotal),
    },
  ]

  const psidPaymentModeKey = useMemo(() => resolvePsidPaymentModeKey(modeOfPayments), [modeOfPayments])

  const onDepositViaPsidChange = e => {
    const channel = e.target.checked
      ? FRACTIONAL_PAYMENT_CHANNEL_PSID
      : FRACTIONAL_PAYMENT_CHANNEL_STANDARD
    const amount = roundMoneyToTwoDecimals(
      parsePaymentLineAmount(form.getFieldValue([PAYMENTS_FORM_LIST_NAME, 0, "amount"]) ?? grandTotal) || 0
    )
    form.setFieldsValue({
      fractionalPaymentChannel: channel,
      [PAYMENTS_FORM_LIST_NAME]: [freshFractionalPaymentRowAfterChannelToggle(amount)],
    })
  }

  const cardLabel = (
    <Flex justify="space-between" align="center">
      <Text type="secondary">{t("Sale summary")}</Text>
      {!!psidPaymentModeKey && (
        <span onClick={e => e.stopPropagation()} role="presentation">
          <Form.Item
            name="fractionalPaymentChannel"
            style={{ marginTop: 12, marginBottom: 8 }}
            valuePropName="checked"
            getValueFromEvent={ev =>
              ev.target.checked ? FRACTIONAL_PAYMENT_CHANNEL_PSID : FRACTIONAL_PAYMENT_CHANNEL_STANDARD
            }
            getValueProps={value => ({
              checked: value === FRACTIONAL_PAYMENT_CHANNEL_PSID,
            })}
          >
            <Checkbox onChange={onDepositViaPsidChange}>{t("Deposit via PSID")}</Checkbox>
          </Form.Item>
        </span>
      )}
    </Flex>
  )

  return (
    <Card
      Shadow={false}
      className={styles.blocksSummaryCard}
      variant="gray"
      style={{ marginTop: 12, borderRadius: 8 }}
      title={cardLabel}
      headStyle={{
        background: theme["gray100"],
      }}
      bodyStyle={{ padding: "12px 16px" }}
      styles={{ "--card-padding-block": "0px" }}
    >
      {rows.map(item => (
        <Flex key={item.label} justify="space-between" className={"mbe-4"}>
          <Text type="secondary">{item.label}</Text>
          {item.value}
        </Flex>
      ))}
      <Divider style={{ margin: "8px 0" }} />
      <Flex justify="space-between" className={"mbe-4"}>
        <Text type="secondary">{t("Grand total")}</Text>
        <span className={styles.grandTotalValue}>{formatFractionalSaleAmount(grandTotal)}</span>
      </Flex>
    </Card>
  )
}
